/*
 * Copyright Mimic Networks, Inc. 2025.
 */

import { Form, FormInstance } from 'antd';
import { debounce } from 'lodash';
import { useCallback, useMemo, useState } from 'react';

import { Subtenant } from '@/client';
import { ValidationError } from '@/components/Forms/ValidationError';
import { usePaginatedSubtenantsQuery } from '@/hooks/query/usePaginatedSubtenantsQuery';
import { useMimicTranslation } from '@/hooks/useMimicTranslation';
import { Input } from '@/primitives/Input';
import { Select } from '@/primitives/Select';
import { TextArea } from '@/primitives/TextArea';
import { tokens } from '@/theme';

export type SubtenantFormProps = {
  subtenant?: Subtenant;
  loading: boolean;
  form: FormInstance<SubtenantFormFields>;
};

export type SubtenantFormFields = {
  name: string;
  description: string;
  subtenantId?: string;
};

export function SubtenantForm({ subtenant, loading, form }: SubtenantFormProps) {
  const { t } = useMimicTranslation('subtenants');
  const nameMaxLength = 30;
  const subtenantMaxLength = 280;
  const [searchValue, setSearchValue] = useState<string>('');
  const [searchInput, setSearchInput] = useState<string>('');

  const subtenantsQuery = usePaginatedSubtenantsQuery({
    params: {
      flatten: true,
      pageSize: 100,
      filters: searchValue ? { name: searchValue } : undefined,
    },
    type: 'inMemory',
  });

  const subtenantOptions =
    subtenantsQuery.data?.data
      .filter((w) => w.id !== subtenant?.id) // Subtenant can't point to itself
      .map((w) => ({
        label: w.name,
        value: w.id,
      })) || [];

  const debouncedSearch = useMemo(
    () =>
      debounce((value: string) => {
        setSearchValue(value);
      }, 300),
    [],
  );

  const handleSearch = useCallback(
    (value: string) => {
      setSearchInput(value);
      debouncedSearch(value);
    },
    [debouncedSearch],
  );

  const handleSelect = useCallback(() => {
    setSearchInput('');
    setSearchValue('');
  }, []);

  return (
    <Form
      form={form}
      autoComplete="off"
      initialValues={{
        name: subtenant?.name,
        description: subtenant?.description || '',
        subtenantId: subtenant?.parent?.id,
      }}
      layout="vertical"
      requiredMark={requiredMarkAfter}
      data-testid="subtenant-form"
      validateTrigger="onBlur"
    >
      <Form.Item<SubtenantFormFields>
        label={t('addSubtenant.subtenantName')}
        status="error"
        name="name"
        rules={[
          { required: true, message: <ValidationError>{t('subtenantForm.nameRequired')}</ValidationError> },
          {
            max: nameMaxLength,
            message: (
              <ValidationError>{t('subtenantForm.nameMaxLength', { maxLength: nameMaxLength })}</ValidationError>
            ),
          },
        ]}
      >
        <Input
          autoFocus
          disabled={loading}
          placeholder={t('addSubtenant.namePlaceholder')}
          allowClear
          dataTestId="subtenant-name-input"
          count={{
            max: nameMaxLength,
          }}
        />
      </Form.Item>
      <Form.Item<SubtenantFormFields>
        label={t('addSubtenant.description')}
        status="error"
        name="description"
        rules={[
          {
            max: subtenantMaxLength,
            message: (
              <ValidationError>
                {t('subtenantForm.descriptionMaxLength', { maxLength: subtenantMaxLength })}
              </ValidationError>
            ),
          },
        ]}
      >
        <TextArea
          allowClear
          rows={4}
          dataTestId="subtenant-description-input"
          count={{ show: true, max: subtenantMaxLength }}
          maxLength={subtenantMaxLength}
          style={{
            backgroundColor: tokens.color.surface.surfaceHigh,
          }}
        />
      </Form.Item>
      <Form.Item<SubtenantFormFields> label={t('addSubtenant.parentSubtenant')} name="subtenantId">
        <Select
          placeholder={t('addSubtenant.parentSubtenantPlaceholder')}
          options={subtenantOptions}
          allowClear
          showSearch
          searchValue={searchInput}
          onSearch={handleSearch}
          onChange={(value) => {
            if (value) {
              handleSelect();
            }
          }}
          onClear={handleSelect}
          filterOption={false}
          notFoundContent={
            subtenantsQuery.isLoading
              ? t('addSubtenant.parentSubtenantLoading')
              : t('addSubtenant.parentSubtenantNotFound')
          }
          disabled={loading}
          data-testid="parent-subtenant-select"
        />
      </Form.Item>
    </Form>
  );
}

const requiredMarkAfter = (label: React.ReactNode) => label;
